import { useState } from "react";
import { Link } from "react-router-dom";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const FORM_URL = "https://readdy.ai/api/form/db7tfpqtp9sllopf7jv0";

const contactInfo = [
  {
    icon: "ri-mail-line",
    label: "反馈邮箱",
    value: "support@bitebi-video.com",
    desc: "内容纠错、功能建议与合作咨询",
    style: "bg-primary-100 text-primary-600",
  },
  {
    icon: "ri-time-line",
    label: "回应时间",
    value: "工作日 24 小时内",
    desc: "我们会尽快查看并回复每一条留言",
    style: "bg-accent-100 text-accent-700",
  },
  {
    icon: "ri-shield-check-line",
    label: "内容声明",
    value: "演示数据 · 无版权风险",
    desc: "站内视频与图片均为示例内容，不代表真实来源",
    style: "bg-secondary-100 text-secondary-700",
  },
];

export default function ContactPage() {
  useDocumentMeta({
    title: "联系我们 - 逼特逼视频",
    description:
      "通过留言反馈与逼特逼视频团队取得联系，提交内容纠错、功能建议或合作咨询，我们会尽快回复。",
    keywords: "逼特逼视频,联系我们,反馈,合作,留言",
  });

  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [fieldError, setFieldError] = useState("");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const name = String(formData.get("name") ?? "").trim();
    const email = String(formData.get("email") ?? "").trim();
    const message = String(formData.get("message") ?? "").trim();

    if (!name) {
      setFieldError("请填写你的称呼。");
      return;
    }
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setFieldError("请填写有效的邮箱地址。");
      return;
    }
    if (!message) {
      setFieldError("请填写留言内容。");
      return;
    }
    if (message.length > 500) {
      setFieldError("留言内容不能超过 500 个字符。");
      return;
    }

    setFieldError("");

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") {
        return;
      }
      payload.append(key, value.toString());
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const code = parsed?.code;
      const isSpam = String(serverMsg).toLowerCase().includes("spam");

      if (response.ok && code === "OK" && !isSpam) {
        setStatus("success");
        setFormError("");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <div className="mx-auto w-full max-w-[1440px] px-4 py-6 md:px-8">
      <section className="rounded-lg border border-background-200 bg-background-100 p-6 md:p-10">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
          <i className="ri-customer-service-2-line" />
          联系我们
        </span>
        <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
          与逼特逼视频保持联系
        </h1>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
          逼特逼视频是一个多样视频内容聚合平台，聚合影视剪辑、短视频、用户原创作品等多种类型内容，帮助用户快速浏览与发现喜欢的视频。无论你是想反馈内容问题、提出功能建议，还是希望与我们交流合作，都可以通过下方表单留言。
        </p>
      </section>

      <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_1.3fr]">
        <div className="flex flex-col gap-4">
          {contactInfo.map((item) => (
            <div
              key={item.label}
              className="flex items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-5"
            >
              <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${item.style}`}>
                <i className={`${item.icon} text-xl`} />
              </span>
              <div>
                <p className="text-xs text-foreground-500">{item.label}</p>
                <p className="mt-0.5 text-sm font-medium text-foreground-900">{item.value}</p>
                <p className="mt-1 text-xs leading-relaxed text-foreground-500">{item.desc}</p>
              </div>
            </div>
          ))}

          <div className="rounded-lg border border-background-200 bg-secondary-50 p-5">
            <h2 className="flex items-center gap-1.5 text-sm font-medium text-secondary-900">
              <i className="ri-lightbulb-line" />
              关于演示性质
            </h2>
            <p className="mt-2 text-xs leading-relaxed text-secondary-800">
              本站的视频浏览、搜索、筛选与播放均为前端演示功能，不接入真实接口；留言反馈为真实可提交表单，提交后我们会收到你的信息。
            </p>
          </div>
        </div>

        <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
          <h2 className="text-lg font-semibold text-foreground-950">留言反馈</h2>
          <p className="mt-1.5 text-sm text-foreground-500">带 * 的为必填项，留言内容不超过 500 字。</p>

          {status === "success" ? (
            <div className="mt-6 flex flex-col items-center rounded-lg border border-primary-200 bg-primary-50 px-6 py-12 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-500 text-background-50">
                <i className="ri-check-line text-3xl" />
              </span>
              <h3 className="mt-4 text-base font-medium text-foreground-900">提交成功，感谢你的反馈！</h3>
              <p className="mt-2 max-w-sm text-sm text-foreground-500">
                我们已经收到你的留言，会尽快查看并回复到你的邮箱。
              </p>
              <button
                type="button"
                onClick={() => setStatus("idle")}
                className="mt-5 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2 text-sm text-background-50 transition-colors hover:bg-primary-600"
              >
                再写一条
              </button>
            </div>
          ) : (
            <form
              id="contact-feedback-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="mt-6 flex flex-col gap-5"
            >
              <div>
                <label htmlFor="contact-name" className="mb-1.5 block text-sm text-foreground-700">
                  你的称呼 <span className="text-accent-600">*</span>
                </label>
                <input
                  id="contact-name"
                  name="name"
                  type="text"
                  required
                  placeholder="怎么称呼你？"
                  className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                />
              </div>

              <div>
                <label htmlFor="contact-email" className="mb-1.5 block text-sm text-foreground-700">
                  邮箱 <span className="text-accent-600">*</span>
                </label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  required
                  placeholder="方便我们回复你的邮箱"
                  className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                />
              </div>

              <div>
                <label htmlFor="contact-message" className="mb-1.5 block text-sm text-foreground-700">
                  留言内容 <span className="text-accent-600">*</span>
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="请描述你的问题、建议或合作意向（500 字以内）"
                  className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                />
              </div>

              <input
                className="bvb-form-guard"
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              {fieldError && (
                <p className="flex items-center gap-1.5 text-sm text-accent-700">
                  <i className="ri-error-warning-line" />
                  {fieldError}
                </p>
              )}

              {status === "error" && formError && (
                <p className="flex items-center gap-1.5 text-sm text-accent-700">
                  <i className="ri-error-warning-line" />
                  {formError}
                </p>
              )}

              <div className="flex flex-wrap items-center gap-4">
                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {status === "submitting" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      提交中…
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line" />
                      提交留言
                    </>
                  )}
                </button>
                <Link
                  to="/"
                  className="cursor-pointer whitespace-nowrap text-sm text-foreground-500 transition-colors hover:text-primary-600"
                >
                  返回首页
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}